import { useResolveMetadataKey } from '@modules/ManageUnifiedFields/hooks/useResolveMetadataKey';
import { mapEQLReferenceToSelectedMetadata } from '@modules/ManageUnifiedFields/utils/mapEQLReferenceToSelectedMetadata';
import { CanAccess } from '@modules/common/components/CanAccess';
import { UnifiedFieldIcon } from '@modules/common/components/UnifiedFieldIcon';
import { trackIntegrationPageManageMetadataEvent } from '@modules/integrationsV2/utils';
import { EditRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useEffect } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

interface MetadataUnifiedFieldsTooltipParams {
  metadataKey: string;
  onEditUnifiedFieldButtonClick?: (metadataKey?: string) => void;
  shouldUpdateMetadataUnifiedFields?: boolean;
}

export const MetadataUnifiedFieldsTooltip = ({
  metadataKey,
  onEditUnifiedFieldButtonClick,
  shouldUpdateMetadataUnifiedFields,
}: MetadataUnifiedFieldsTooltipParams) => {
  const { metadata, status, refetch } = useResolveMetadataKey(metadataKey);
  const metadataEqlFieldRefrences =
    mapEQLReferenceToSelectedMetadata(metadata?.references, metadata.type) ??
    [];

  useEffect(() => {
    if (shouldUpdateMetadataUnifiedFields) {
      refetch();
    }
  }, [shouldUpdateMetadataUnifiedFields, refetch]);

  const getTooltipTitle = () => {
    if (IsLoading(status)) {
      return (
        <LinearProgress
          variant="indeterminate"
          sx={{
            borderRadius: 4,
            my: 3,
          }}
        />
      );
    }

    return (
      <Box justifyContent="center" p={0.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          alignItems="center"
        >
          <Typography variant="caption">Part of Unified Field:</Typography>
          <CanAccess
            resource={PermissionResource.Metadata}
            operation={PermissionOperation.Write}
            wrapper="inline"
          >
            <Button
              variant="text"
              color="primary"
              endIcon={
                <EditRounded
                  sx={{
                    fontSize: '0.8rem !important',
                  }}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                onEditUnifiedFieldButtonClick?.(metadataKey);
                trackIntegrationPageManageMetadataEvent({
                  eventName:
                    'Manage Metadata Table - Edit Unified Field Button Clicked from Tooltip',
                });
              }}
            >
              {metadata?.displayText}
            </Button>
          </CanAccess>
        </Stack>
        <Paper sx={{ p: 0.5, mt: 0.5 }}>
          <List
            sx={{
              overflow: 'auto',
              maxHeight: 150,
            }}
          >
            {metadataEqlFieldRefrences?.map((metadataEqlFieldRefrence) => (
              <ListItem key={metadataEqlFieldRefrence.id} disablePadding>
                <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                  <Box
                    component="img"
                    width="24px"
                    height="auto"
                    src={metadataEqlFieldRefrence.sourceIconURL}
                    alt={`icon ${metadata.source}`}
                    loading="lazy"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="caption" display={'block'}>
                      {metadataEqlFieldRefrence.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" display={'block'}>
                      From {metadataEqlFieldRefrence.source}
                    </Typography>
                  }
                  sx={{ ml: 1 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    );
  };

  return (
    <Tooltip
      title={getTooltipTitle()}
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            minWidth: '300px',
            maxWidth: '500px',
          },
        },
      }}
    >
      <IconButton>
        <UnifiedFieldIcon sx={{ color: 'info.main' }} />
      </IconButton>
    </Tooltip>
  );
};
